import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { API, toFullUrl } from '../../config/api';
import { Gift, Plus, Trash2, Trophy, Loader2, Calendar, X, Upload, Image as ImageIcon, Users, Edit, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_TIERS = [
  { price: 1.0, entries: 1, label: '1 ticket' },
  { price: 5.0, entries: 7, label: '7 tickets' },
  { price: 10.0, entries: 15, label: '15 tickets' },
];

export function RafflesTab() {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRaffle, setEditingRaffle] = useState(null);

  const fetchRaffles = async () => {
    try {
      const { data } = await api.get(`${API}/raffles?active_only=false`);
      setRaffles(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load raffles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRaffles(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this raffle and all entries? Cannot be undone.')) return;
    try {
      await api.delete(`${API}/raffles/${id}`);
      setRaffles((r) => r.filter((x) => x.id !== id));
      toast.success('Raffle deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDraw = async (id) => {
    if (!window.confirm('Pick a random winner now? This closes the raffle.')) return;
    try {
      const { data } = await api.post(`${API}/raffles/${id}/draw`);
      toast.success(`Winner: ${data.winner.email}`);
      fetchRaffles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Draw failed');
    }
  };

  const closeDialog = () => { setDialogOpen(false); setEditingRaffle(null); };
  const onSaved = () => { fetchRaffles(); closeDialog(); };

  return (
    <div className="space-y-6" data-testid="raffles-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
          <Gift className="w-6 h-6 text-[#DAA520]" /> RAFFLES
        </h2>
        <button onClick={() => { setEditingRaffle(null); setDialogOpen(true); }}
          className="px-4 py-2 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase text-sm flex items-center gap-2 transition-colors"
          data-testid="new-raffle-btn">
          <Plus className="w-4 h-4" /> NEW RAFFLE
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : raffles.length === 0 ? (
        <div className="py-16 text-center bg-[#141414] border border-[#262626] rounded-xl">
          <Gift className="w-12 h-12 text-[#262626] mx-auto mb-3" />
          <p className="text-sm text-[#737373]">No raffles yet. Click <span className="text-[#DAA520] font-bold">NEW RAFFLE</span> to start your first giveaway.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {raffles.map((r) => {
            const tiers = (r.paid_tiers && r.paid_tiers.length) ? r.paid_tiers : DEFAULT_TIERS;
            return (
              <div key={r.id} className={`bg-[#141414] border-2 ${r.status === 'completed' ? 'border-[#22C55E]/40' : 'border-[#DAA520]/30'} overflow-hidden`} data-testid={`raffle-card-${r.id}`}>
                {r.cover_image && <img src={toFullUrl(r.cover_image)} alt={r.title} className="w-full aspect-video object-cover" />}
                <div className="p-4 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-lg">{r.title}</h3>
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'completed' ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-[#DAA520]/20 text-[#DAA520]'}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#A3A3A3]">Prize: <span className="text-white font-bold">{r.prize}</span></p>
                    {r.end_date && <p className="text-[10px] text-[#525252] mt-0.5">Ends {r.end_date}</p>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#A3A3A3]">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.entry_count} entries</span>
                    <span className="flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> {tiers.length} tier{tiers.length === 1 ? '' : 's'}</span>
                  </div>
                  {r.winner && (
                    <div className="bg-[#0A1F0F] border border-[#22C55E]/30 p-2 rounded text-xs">
                      <p className="text-[#22C55E] font-bold uppercase text-[10px]"><Trophy className="w-3 h-3 inline mr-1" /> Winner</p>
                      <p className="text-white font-mono">{r.winner.email}</p>
                      {r.winner.ticket && <p className="text-[10px] text-[#A3A3A3]">Ticket: {r.winner.ticket}</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {r.status === 'active' && r.entry_count > 0 && (
                      <button onClick={() => handleDraw(r.id)}
                        className="flex-1 px-3 py-2 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black text-xs font-bold uppercase flex items-center justify-center gap-1.5 transition-colors"
                        data-testid={`draw-${r.id}`}>
                        <Trophy className="w-3.5 h-3.5" /> DRAW WINNER
                      </button>
                    )}
                    <button onClick={() => { setEditingRaffle(r); setDialogOpen(true); }}
                      className="px-2 py-2 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520] transition-colors"
                      title="Edit raffle (tiers, title, etc.)"
                      data-testid={`edit-raffle-${r.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(r.id)}
                      className="px-2 py-2 border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                      data-testid={`delete-raffle-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialogOpen && <RaffleDialog raffle={editingRaffle} onClose={closeDialog} onSaved={onSaved} />}
    </div>
  );
}

function RaffleDialog({ raffle, onClose, onSaved }) {
  const isEdit = !!raffle;
  const [form, setForm] = useState({
    title: raffle?.title || '',
    prize: raffle?.prize || '',
    description: raffle?.description || '',
    cover_image: raffle?.cover_image || '',
    end_date: raffle?.end_date || '',
    entry_limit_per_email: raffle?.entry_limit_per_email ?? 1,
  });
  const [tiers, setTiers] = useState(
    raffle?.paid_tiers && raffle.paid_tiers.length
      ? raffle.paid_tiers.map(t => ({ price: t.price, entries: t.entries, label: t.label || '' }))
      : DEFAULT_TIERS.map(t => ({ ...t }))
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`${API}/upload/cover`, fd);
      setForm((f) => ({ ...f, cover_image: data.url || data.path }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateTier = (idx, field, value) => {
    setTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const removeTier = (idx) => setTiers(prev => prev.filter((_, i) => i !== idx));
  const addTier = () => {
    if (tiers.length >= 8) { toast.error('Max 8 tiers'); return; }
    setTiers(prev => [...prev, { price: 5, entries: 5, label: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.prize.trim()) {
      toast.error('Title and prize are required'); return;
    }
    // Validate tiers
    const cleanedTiers = [];
    for (const t of tiers) {
      const price = parseFloat(t.price);
      const entries = parseInt(t.entries);
      if (!(price > 0) || !(entries > 0)) {
        toast.error('Each tier needs price > 0 and entries > 0'); return;
      }
      cleanedTiers.push({
        price: Math.round(price * 100) / 100,
        entries,
        label: (t.label || '').trim() || `${entries} ticket${entries === 1 ? '' : 's'}`,
      });
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        entry_limit_per_email: parseInt(form.entry_limit_per_email) || 1,
        paid_tiers: cleanedTiers,
      };
      if (isEdit) {
        await api.put(`${API}/raffles/${raffle.id}`, payload);
        toast.success('Raffle updated');
      } else {
        await api.post(`${API}/raffles`, payload);
        toast.success('Raffle created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border-2 border-[#DAA520]/40 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="raffle-dialog">
        <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 flex items-center justify-between z-10">
          <h3 className="font-cabinet font-black uppercase text-lg flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#DAA520]" /> {isEdit ? 'EDIT RAFFLE' : 'NEW RAFFLE'}
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Cover Image (optional)</label>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex items-center justify-center shrink-0">
                {form.cover_image
                  ? <img src={toFullUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-6 h-6 text-[#262626]" />}
              </div>
              <label className="flex-1 h-10 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520] text-xs font-bold uppercase flex items-center justify-center gap-2 cursor-pointer transition-colors">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCover(e.target.files?.[0])} />
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> {form.cover_image ? 'Change' : 'Upload'}</>}
              </label>
            </div>
            <p className="text-[10px] text-[#525252]">Recommended: 1200×675px (16:9 aspect ratio) · max 5MB</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block mb-1">Title *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={120}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm" data-testid="raffle-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block mb-1">Prize *</label>
            <input value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} required maxLength={200}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm" data-testid="raffle-prize"
              placeholder="e.g. Signed vinyl + 2 concert tickets" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={500}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm resize-none" data-testid="raffle-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> End Date</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm" data-testid="raffle-end-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block mb-1">Free Entries / Email</label>
              <input type="number" min="1" max="10" value={form.entry_limit_per_email} onChange={(e) => setForm({ ...form, entry_limit_per_email: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm font-mono" data-testid="raffle-limit" />
            </div>
          </div>

          {/* Tier Editor */}
          <div className="border border-[#262626] bg-[#0A0A0A] p-3 space-y-3" data-testid="raffle-tier-editor">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#DAA520] font-bold flex items-center gap-1.5">
                  <Ticket className="w-3.5 h-3.5" /> Paid Entry Tiers
                </label>
                <p className="text-[10px] text-[#525252] mt-0.5">Bonus tickets fans can buy via Stripe. Leave empty to disable paid entries.</p>
              </div>
              <button type="button" onClick={addTier}
                className="text-[10px] uppercase font-bold px-2 py-1.5 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520] transition-colors flex items-center gap-1"
                data-testid="add-tier-btn">
                <Plus className="w-3 h-3" /> ADD TIER
              </button>
            </div>

            {tiers.length === 0 ? (
              <p className="text-[10px] text-[#525252] text-center py-3">No paid tiers · free-entry only</p>
            ) : (
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 text-[9px] uppercase tracking-wider text-[#525252] px-1">
                  <span>Price (USD)</span>
                  <span>Tickets</span>
                  <span>Label (shown to fans)</span>
                  <span />
                </div>
                {tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center" data-testid={`tier-row-${i}`}>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#525252]">$</span>
                      <input type="number" min="0.5" step="0.5" value={t.price}
                        onChange={(e) => updateTier(i, 'price', e.target.value)}
                        className="w-full bg-[#141414] border border-[#262626] focus:border-[#DAA520] outline-none pl-5 pr-2 py-1.5 text-xs font-mono"
                        data-testid={`tier-price-${i}`} />
                    </div>
                    <input type="number" min="1" step="1" value={t.entries}
                      onChange={(e) => updateTier(i, 'entries', e.target.value)}
                      className="w-full bg-[#141414] border border-[#262626] focus:border-[#DAA520] outline-none px-2 py-1.5 text-xs font-mono"
                      data-testid={`tier-entries-${i}`} />
                    <input type="text" placeholder={`${t.entries} tickets`} value={t.label}
                      onChange={(e) => updateTier(i, 'label', e.target.value)}
                      maxLength={40}
                      className="w-full bg-[#141414] border border-[#262626] focus:border-[#DAA520] outline-none px-2 py-1.5 text-xs"
                      data-testid={`tier-label-${i}`} />
                    <button type="button" onClick={() => removeTier(i)}
                      className="w-8 h-8 flex items-center justify-center border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                      title="Remove tier"
                      data-testid={`tier-remove-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={saving}
            className="w-full h-12 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            data-testid="raffle-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {isEdit ? 'Saving…' : 'Creating…'}</> : (isEdit ? 'SAVE CHANGES' : 'CREATE RAFFLE')}
          </button>
        </form>
      </div>
    </div>
  );
}
